import { createContext } from 'react';

import type {
  AccessUser,
  WithoutPermission,
  WithoutRestriction,
  WithPermission,
  WithRestriction,
} from '@/entities/user/types';

/**
 * Access surface exposed to the authenticated app (consumed via `useAccess()`).
 *
 * - `user` — the `/users/me` payload ({@link AccessUser}).
 * - `hasPermission(...ps)` — any-of dot-path check against `role.permissions`.
 * - `hasAccess(p)` — tolerant single-rule check (`undefined` ⇒ allowed,
 *   array ⇒ any-of). Same shape as `WithPermission.permission`.
 * - `filterByPermissions/filterByRestrictions` — declarative list filtering
 *   (nav menus, action lists), recursing into nested `items`.
 * - `refresh()` — refetch `/users/me` (e.g. after profile/role updates).
 *
 * UG-4498 (router) consumes this together with
 * `checkRouteAccess(pathname, user)` from `@/config/auth/routeAccess`.
 */
export interface AccessContextType {
  user: AccessUser;
  hasPermission: (...permissions: string[]) => boolean;
  hasAccess: (permission?: string | string[]) => boolean;
  filterByPermissions: <T>(list: WithPermission<T>[]) => WithoutPermission<T>[];
  filterByRestrictions: <T>(list: WithRestriction<T>[]) => WithoutRestriction<T>[];
  refresh: () => Promise<void>;
}

export const AccessContext = createContext<AccessContextType | null>(null);

/** Query key of the `POST /users/me` session query (for cache invalidation). */
export const meQueryKey = ['users', 'me'] as const;
